import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  hasMapCalculationLayers,
  MapCalculationConfigHashSuffix,
} from '../../createMapSchema.ts';
import { type IMapWidget } from '../../IMapWidget.ts';
import { computeIntersectionsCompetencesConfigHash } from './utils/computeIntersectionsCompetencesConfigHash.ts';

export interface IUseMapCalculationConfigHashOptions {
  /** Configurazione del widget da cui deriva l'hash */
  widget: IMapWidget;
  /** Nome campo base del widget */
  fieldName: string;
}

/**
 * Hash della configurazione di intersezioni e competenze, salvato accanto
 * alle tabelle per sapere se sono state calcolate con la configurazione
 * attuale. Vuoto durante un calcolo o dopo un errore; assente nei dati
 * precedenti all'hash.
 */
export function useMapCalculationConfigHash(
  options: IUseMapCalculationConfigHashOptions,
) {
  const { widget, fieldName } = options;
  const { getValues } = useFormContext();
  const setValue = useFormSetValue();

  const configHashFieldName = `${fieldName}${MapCalculationConfigHashSuffix}`;

  const hasCalculationLayers = useMemo(
    () => hasMapCalculationLayers(widget),
    [widget],
  );
  const configHash = useMemo(
    () => computeIntersectionsCompetencesConfigHash(widget),
    [widget],
  );

  /** True se l'hash salvato è assente, vuoto o di un'altra configurazione. */
  const isSavedHashOutdated = useCallback(
    () => hasCalculationLayers && getValues(configHashFieldName) !== configHash,
    [hasCalculationLayers, configHashFieldName, configHash, getValues],
  );

  /**
   * Imposta l'hash vuoto. Non usa `null` perché l'hash assente identifica i
   * dati salvati prima della sua introduzione, che la regola accetta.
   */
  const invalidateConfigHash = useCallback(() => {
    if (!hasCalculationLayers) return;
    if (getValues(configHashFieldName) === '') return;
    setValue(configHashFieldName, '', { shouldDirty: false });
  }, [hasCalculationLayers, configHashFieldName, getValues, setValue]);

  /**
   * Registra l'hash corrente. Il form diventa modificato solo se l'hash è
   * diverso da quello salvato, così Salva si abilita anche a tabelle
   * identiche: il confronto con il valore iniziale lo fa react-hook-form.
   */
  const storeConfigHash = useCallback(() => {
    if (!hasCalculationLayers) return;
    if (getValues(configHashFieldName) === configHash) return;
    setValue(configHashFieldName, configHash, { shouldDirty: true });
  }, [
    hasCalculationLayers,
    configHashFieldName,
    configHash,
    getValues,
    setValue,
  ]);

  return { isSavedHashOutdated, invalidateConfigHash, storeConfigHash };
}
